import * as moment from 'moment';
import { createSelector } from 'reselect';
import { UserPlan } from '~/types/Plan';
import { ApplicationState } from '../store';
import { isWixConnected } from './stores.selectors';
import { PAYG, PAYG_30_LABEL_LIMIT } from '~/constants/plans';

const STARTER = 'STARTER';
const GROWTH = 'GROWTH';
const GRANDFATHER = 'GRANDFATHER';
const PREMIER = 'PREMIER';
const PREMIUM = 'PREMIUM';

export const getPlansStore = (state: ApplicationState) => state.plans;

export const getCurrentPlan = createSelector(getPlansStore, (plansStore) => plansStore.currentPlan);

export const getNextPlan = createSelector(getPlansStore, (plansStore) => plansStore.nextPlan);

export const getSelectedPlan = createSelector(
  // Next plan if there is one, otherwise current plan
  getCurrentPlan,
  getNextPlan,
  (currentPlan, nextPlan) => nextPlan || currentPlan,
);

export const getAdjustedPlan = createSelector(
  getPlansStore,
  (plansStore) => plansStore.adjustedPlan,
);

export const getCurrentPlanDisplayName = createSelector(
  getPlansStore,
  (plansStore): string | null =>
    plansStore.currentPlan ? plansStore.currentPlan.plan.displayName : null,
);

export const getIsChangingPlans = createSelector(getPlansStore, (plansStore) => {
  if (plansStore.nextPlan) {
    return true;
  }
  return false;
});

export const getFetchPlanRequest = createSelector(
  getPlansStore,
  (plansStore) => plansStore.fetchPlanRequest,
);

export const getFetchPricingPlansRequest = createSelector(
  getPlansStore,
  (plansStore) => plansStore.fetchPricingPlansRequest,
);

export const getMonthlyPricingPlans = createSelector(
  getFetchPricingPlansRequest,
  (fetchPricingPlansRequest) => {
    if (fetchPricingPlansRequest.kind !== 'success') {
      return [];
    }

    return fetchPricingPlansRequest.data
      .filter((plan) => plan.billingFrequency === 'MONTHLY')
      .sort((a, b) => a.numberOfLabelsIncluded - b.numberOfLabelsIncluded);
  },
);

export const getYearlyPricingPlans = createSelector(
  getFetchPricingPlansRequest,
  (fetchPricingPlansRequest) => {
    if (fetchPricingPlansRequest.kind !== 'success') {
      return [];
    }

    return fetchPricingPlansRequest.data
      .filter((plan) => plan.billingFrequency === 'YEARLY')
      .sort((a, b) => a.numberOfLabelsIncluded - b.numberOfLabelsIncluded);
  },
);

export const planStartDate = (
  currentPlan?: Readonly<UserPlan | null | undefined>,
): moment.Moment => {
  if (currentPlan) {
    return currentPlan.startDate;
  }

  return moment();
};

export const getPlanStartDate = createSelector(getCurrentPlan, (currentPlan) => {
  return planStartDate(currentPlan);
});

export const getNextPlanStartDate = createSelector(getNextPlan, (nextPlan) => {
  return planStartDate(nextPlan);
});

export const planEndDate = (currentPlan: Readonly<UserPlan | null | undefined>): moment.Moment => {
  if (currentPlan) {
    return currentPlan.endDate!;
  }

  return moment();
};

export const getPlanEndDate = createSelector(getCurrentPlan, (currentPlan) => {
  return planEndDate(currentPlan);
});

export const getNextPlanEndDate = createSelector(getNextPlan, (nextPlan) => {
  return planEndDate(nextPlan);
});

export const getNextBillingDate = createSelector(getPlansStore, (plansStore) => {
  const { currentPlan, nextPlan } = plansStore;
  if (nextPlan?.nextBillingDate) {
    return nextPlan.nextBillingDate;
  }
  if (currentPlan?.nextBillingDate) {
    return currentPlan.nextBillingDate;
  }
  return null;
});

export const billingFrequency = (currentPlan: Readonly<UserPlan | null | undefined>): string => {
  if (currentPlan) {
    return currentPlan.plan.billingFrequency;
  }

  return '';
};

export const getBillingFrequencyOfNextPlan = createSelector(getNextPlan, (nextPlan): string => {
  return billingFrequency(nextPlan);
});

export const getBillingFrequency = createSelector(getCurrentPlan, (currentPlan): string => {
  return billingFrequency(currentPlan);
});

export const originalSubscriptionPrice = (currentPlan: UserPlan): number => {
  return currentPlan.plan.originalSubscriptionPrice;
};

export const getOriginalSubscriptionPrice = createSelector(
  getCurrentPlan,
  (currentPlan): number | null => {
    return currentPlan ? originalSubscriptionPrice(currentPlan!) : null;
  },
);

export const numberOfLabelsIncluded = (
  currentPlan: Readonly<UserPlan | null | undefined>,
): number => {
  if (currentPlan) {
    return currentPlan.plan.numberOfLabelsIncluded;
  }

  return -1;
};

export const getNumberOfLabelsIncludedInNextPlan = createSelector(
  getNextPlan,
  (nextPlan): number => {
    return numberOfLabelsIncluded(nextPlan);
  },
);

export const getNumberOfLabelsIncluded = createSelector(getCurrentPlan, (currentPlan): number => {
  return numberOfLabelsIncluded(currentPlan);
});

export const numberOfSeats = (currentPlan: UserPlan): number => {
  return currentPlan.plan.numberOfSeats;
};

export const getNumberOfSeats = createSelector(getCurrentPlan, (currentPlan): number => {
  return numberOfSeats(currentPlan!);
});

export const getSetPlanRequest = createSelector(
  getPlansStore,
  (plansStore) => plansStore.setPlanRequest,
);

export const userHasFullAccess = (currentPlan: Readonly<UserPlan | null | undefined>): boolean => {
  return (currentPlan && (currentPlan.plan.isPro || currentPlan.plan.isFullAccess)) || false;
};

export const getUserHasFullAccess = createSelector(getCurrentPlan, (currentPlan): boolean => {
  return userHasFullAccess(currentPlan);
});

export const isInGrowthPlan = (currentPlan: Readonly<UserPlan | null | undefined>): boolean => {
  if (currentPlan && currentPlan.plan && currentPlan.plan.isPro) {
    const { displayName } = currentPlan.plan;
    return displayName.toUpperCase().includes(GROWTH);
  }
  return false;
};

export const getIsInGrowthPlan = createSelector(getCurrentPlan, (currentPlan): boolean => {
  return isInGrowthPlan(currentPlan);
});

export const isInPremierPlan = (currentPlan?: UserPlan | null): boolean => {
  if (currentPlan) {
    const { displayName } = currentPlan.plan;
    return (
      displayName.toUpperCase().includes(PREMIER) || displayName.toUpperCase().includes(PREMIUM)
    );
  }
  return false;
};

export const getIsInPremierPlan = createSelector(getCurrentPlan, (currentPlan): boolean => {
  return isInPremierPlan(currentPlan);
});

export const isInProfessionalPlan = (currentPlan?: UserPlan | null): boolean => {
  if (currentPlan) {
    return !!currentPlan.plan?.isPro && !isInPremierPlan(currentPlan);
  }
  return false;
};

export const getNextPlanIsProfessionalPlan = createSelector(
  getNextPlan,
  getIsInPremierPlan,
  (nextPlan): boolean => {
    return isInProfessionalPlan(nextPlan);
  },
);

export const getSelectedPlanIsProfessionalPlan = createSelector(
  getSelectedPlan,
  (selectedPlan): boolean => {
    return isInProfessionalPlan(selectedPlan);
  },
);

export const getIsInProfessionalPlan = createSelector(
  getCurrentPlan,
  getIsInPremierPlan,
  (currentPlan): boolean => {
    return isInProfessionalPlan(currentPlan);
  },
);

export const isInStarterPlan = (currentPlan?: UserPlan | null): boolean => {
  if (currentPlan) {
    const { displayName } = currentPlan.plan;
    return (
      displayName.toUpperCase().includes(STARTER) ||
      displayName.toUpperCase().includes(PAYG) ||
      displayName.toUpperCase().includes(GRANDFATHER)
    );
  }
  return false;
};

export const getNextPlanIsStarterPlan = createSelector(getNextPlan, (nextPlan): boolean => {
  return isInStarterPlan(nextPlan);
});

export const getSelectedPlanIsStarterPlan = createSelector(
  getSelectedPlan,
  (selectedPlan): boolean => {
    return isInStarterPlan(selectedPlan);
  },
);

export const getIsInStarterPlan = createSelector(getCurrentPlan, (currentPlan): boolean => {
  return isInStarterPlan(currentPlan);
});

export const daysLeftInTrial = (currentPlan: Readonly<UserPlan | null | undefined>): number => {
  if (currentPlan && isInTrialPlan(currentPlan)) {
    const endDateMoment = moment(currentPlan.endDate!);
    return endDateMoment.diff(moment(), 'days');
  }
  return 0;
};

export const getDaysLeftInTrial = createSelector(getCurrentPlan, (currentPlan): number => {
  return daysLeftInTrial(currentPlan);
});

export const isInTrialPlan = (currentPlan: Readonly<UserPlan | null | undefined>): boolean => {
  if (currentPlan) {
    return currentPlan.plan.isFreeTrial;
  }
  return false;
};

export const getIsInTrialPlan = createSelector(getCurrentPlan, (currentPlan): boolean => {
  return isInTrialPlan(currentPlan);
});

export const isInFreePlan = (currentPlan: Readonly<UserPlan | null | undefined>): boolean => {
  if (currentPlan) {
    const { displayName, isFreeTrial } = currentPlan.plan;
    return (
      isFreeTrial ||
      displayName.toUpperCase().includes(GRANDFATHER) ||
      displayName.toUpperCase().includes(PAYG) ||
      displayName.toUpperCase().includes(STARTER)
    );
  }
  return false;
};

export const getIsInFreePlan = createSelector(getCurrentPlan, (currentPlan): boolean => {
  return isInFreePlan(currentPlan);
});

export const getIsInWixProPlan = createSelector(
  getCurrentPlan,
  isWixConnected,
  (currentPlan, isWixConnected): boolean => {
    return currentPlan?.plan.displayName === 'Pro Free' && isWixConnected;
  },
);

export const getIsInNewWixProPlan = createSelector(
  getIsInWixProPlan,
  getCurrentPlan,
  (isInWixProPlan, currentPlan): boolean => {
    return (
      isInWixProPlan &&
      currentPlan?.startDate !== undefined &&
      moment().diff(currentPlan.startDate, 'days') < 3
    );
  },
);

export const hasClosedWixProPlanNotification = createSelector(getPlansStore, (state): boolean => {
  return state.hasClosedWixProPlanNotification;
});

export const isCloseAccountModalOpen = createSelector(
  getPlansStore,
  (plansStore): boolean => plansStore.closeAccountModalIsOpen,
);

export const getCloseAccountError = createSelector(
  getPlansStore,
  (plansStore) => plansStore.closeAccountError,
);

export const getUserPlanIsLoaded = createSelector(getPlansStore, (plansStore) => {
  const request = plansStore.fetchPlanRequest;
  return request?.kind === 'success';
});

export const getIsPAYG = createSelector(
  getPlansStore,
  (plansStore): boolean => plansStore?.currentPlan?.plan?.name === PAYG,
);

export const getIsPAYG30LabelLimit = createSelector(
  getPlansStore,
  (plansStore): boolean => plansStore?.currentPlan?.plan?.name === PAYG_30_LABEL_LIMIT,
);
